import { conferirServicoNaLinha, mensagemProblemaServico, type LinhaServicoMatch } from './servicoNaLinha';
import { supabase } from '@/integrations/supabase/client';
import { ehNumeroDaCasa, MENSAGEM_NUMERO_DA_CASA } from '@/lib/numerosDaCasa';
import { renovacaoExigeAparelho, renovacaoMantemPacote, tmDaRenovacao } from '@/lib/carteiraClaro/calcularAptidaoCarteira';

export interface ValidacaoLeadResult {
  valido: boolean;
  erro?: string;
  descricao?: string;
}

interface LeadValidado {
  id: string;
  contato_real_nome: string | null;
  contato_real_telefone: string | null;
  pedido_id: string | null;
  status_lead: string | null;
  tipo_lead: string | null;
}

async function validarRenovacaoParaPedido(leadId: string): Promise<ValidacaoLeadResult> {
  const { data: propostaAtual, error: propostaError } = await supabase
    .from('leads_carteira')
    .select('proposta_composicao_cnpj, tipo_pessoa')
    .eq('id', leadId)
    .maybeSingle();

  if (propostaError) {
    return {
      valido: false,
      erro: 'Não foi possível conferir a proposta',
      descricao: 'Atualize a tela e tente criar o pedido novamente.',
    };
  }

  const composicaoAtual = propostaAtual?.proposta_composicao_cnpj as { linhas?: Array<{ tipo?: string; numero?: string | null }> } | null;
  // ⛔ CONTAR COM A MESMA RÉGUA DOS DOIS LADOS (Douglas 01/09/2026).
  // O banco só materializa a linha de renovação com msisdn de 10+ dígitos (o
  // trigger sincronizar_renovacao_proposta faz `IF length(v_msisdn) < 10 THEN
  // CONTINUE`). Contando aqui TODA linha RENOVACAO, bastava uma linha sem
  // telefone — e "+ Renov" cria exatamente isso, com o literal 'Renovação' —
  // para os números nunca baterem e o Criar pedido acusar "selecione um plano
  // novo válido para cada linha" com todos os planos escolhidos na tela. Trava
  // muda, sem dizer qual linha era.
  const soDigitosLinha = (v: unknown) => String(v ?? '').replace(/\D/g, '');
  const todasRenovacoesDaProposta = (composicaoAtual?.linhas || []).filter((linha) => linha.tipo === 'RENOVACAO');
  const linhasRenovadasNaProposta = todasRenovacoesDaProposta.filter(
    (linha) => soDigitosLinha(linha.numero).length >= 10,
  );
  const renovacoesSemNumero = todasRenovacoesDaProposta.length - linhasRenovadasNaProposta.length;

  // Sem nenhuma linha de renovação, este validador não tem o que dizer.
  if (todasRenovacoesDaProposta.length === 0) return { valido: true };
  // Com linhas de renovação, mas TODAS sem telefone: o banco não tem o que
  // calcular. Dizer isso na cara, em vez de cair no genérico "simulação
  // incompleta" — que mandava o consultor procurar erro na aba Fatura.
  if (linhasRenovadasNaProposta.length === 0) {
    return {
      valido: false,
      erro: 'Renovação sem telefone',
      descricao: renovacoesSemNumero === 1
        ? 'A linha de renovação está sem número. Preencha o telefone dela na proposta (ou remova a linha) e crie o pedido de novo.'
        : `As ${renovacoesSemNumero} linhas de renovação estão sem número. Preencha os telefones na proposta (ou remova as linhas) e crie o pedido de novo.`,
    };
  }

  // ⛔ NO CPF NÃO EXISTE TM NEM SIMULAÇÃO (Douglas 17/09/2026: "não existe TM
  // no CPF, tire tudo. Nenhuma regra é igual."). O banco já não cria
  // enquadramento para lead PF (migration 20260917211000) — cobrar a simulação
  // aqui trocava a trava do banco por esta, com a mensagem mandando procurar a
  // aba Fatura. O CPF segue só com a checagem de telefone de cima.
  if ((propostaAtual as { tipo_pessoa?: string | null } | null)?.tipo_pessoa === 'PF') {
    return { valido: true };
  }

  const { data, error } = await supabase
    .from('renovacao_enquadramento' as never)
    .select('id, cnpj, fatura_upload_id, renovacao_total, linhas_fatura, linhas_renovadas, flag_convergencia_ato, pedido_fixa_vinculado_id, mes_competencia')
    .eq('lead_id', leadId)
    .maybeSingle();

  if (error || !data) {
    return {
      valido: false,
      erro: 'Simulação da renovação incompleta',
      descricao: 'Abra a aba Fatura, selecione o plano novo de cada linha e confira o simulador antes de criar o pedido.',
    };
  }

  const enquadramento = data as unknown as {
    id: string;
    cnpj: string;
    fatura_upload_id: string | null;
    renovacao_total: boolean;
    linhas_fatura: number;
    linhas_renovadas: number;
    flag_convergencia_ato: 'SIM' | 'NAO' | 'NAO_INFORMADO';
    pedido_fixa_vinculado_id: string | null;
    mes_competencia: string;
  };
  const consultarCarteira = supabase.rpc.bind(supabase) as unknown as (
    funcao: string,
    parametros: Record<string, string>,
  ) => Promise<{ data: Array<{ tempo_contrato_voz?: number | null }> | null }>;
  const consultarContas = supabase.rpc.bind(supabase) as unknown as (
    funcao: string,
    parametros: Record<string, string>,
  ) => Promise<{ data: Array<{ msisdn?: string | null; data_fidelidade?: string | null }> | null }>;
  const [{ data: linhasRenovacao }, { data: leadRenovacao }, { data: carteiraAtual }, { data: linhasContas }] = await Promise.all([
    supabase
      .from('v_renovacao_linha_comercial' as never)
      .select('msisdn, plano_novo, receita_renovada, ticket_atual, faixa_apurada, gb_atual, gb_novo')
      .eq('enquadramento_id', enquadramento.id),
    supabase
      .from('leads_carteira')
      .select('proposta_composicao_cnpj, m_atual, tempo_contrato_voz_num, tempo_contrato_voz')
      .eq('id', leadId)
      .maybeSingle(),
    consultarCarteira('get_carteira_claro_atual', { p_cnpj: enquadramento.cnpj }),
    consultarContas('renovacao_linhas_contas', { p_cnpj: enquadramento.cnpj }),
  ]);
  const linhasCalculadas = (linhasRenovacao || []) as unknown as Array<{
    msisdn: string;
    plano_novo: string | null;
    receita_renovada: number | null;
    ticket_atual: number | null;
    faixa_apurada: number | null;
    gb_atual: number | null;
    gb_novo: number | null;
  }>;
  const leadComTempo = leadRenovacao as unknown as {
    proposta_composicao_cnpj?: unknown;
    m_atual?: number | null;
    tempo_contrato_voz_num?: number | null;
    tempo_contrato_voz?: string | number | null;
  };
  const tempoCarteira = Array.isArray(carteiraAtual)
    ? (carteiraAtual[0] as { tempo_contrato_voz?: number | null } | undefined)?.tempo_contrato_voz
    : null;
  const mesesContrato = Number(
    tempoCarteira
    ?? leadComTempo?.m_atual
    ?? leadComTempo?.tempo_contrato_voz_num
    ?? (String(leadComTempo?.tempo_contrato_voz || '').replace(/\D/g, '') || '0'),
  );
  const pendencias: string[] = [];
  if (!enquadramento.fatura_upload_id) pendencias.push('importe a conta/fatura');
  // ⛔ TM abaixo de 12 NÃO barra (Douglas 01/09/2026): o cliente renova, cai em
  // faixa 7 e não comissiona. Quem carimba a faixa é o banco
  // (sincronizar_renovacao_proposta). O que barra em M12–M15 é o APARELHO:
  // "pode ser um aparelho, mas tem que ter".
  const linhasDaComposicao = (composicaoAtual?.linhas || []) as Array<{ aparelho?: unknown }>;
  const classificacaoCarteira = Array.isArray(carteiraAtual)
    ? String((carteiraAtual[0] as { classificacao?: string | null } | undefined)?.classificacao || '').trim().toUpperCase()
    : '';
  // Régua do TM (Douglas 25/09 e 26/09/2026): M12–M16 só com aparelho; o CG
  // já renova sem aparelho no M15 e M16; do M17 em diante, livre. Na parcial,
  // o TM é o das linhas renovadas, pela fidelidade na conta.
  const regraTm = tmDaRenovacao({
    tmCarteira: mesesContrato,
    parcial: !enquadramento.renovacao_total,
    numerosRenovados: linhasCalculadas.map((linha) => linha.msisdn),
    linhasConta: (Array.isArray(linhasContas) ? linhasContas : []).map((linha) => ({
      numero: linha.msisdn,
      dataFidelidade: linha.data_fidelidade,
    })),
  });
  const mesesRegra = regraTm.tm ?? mesesContrato;
  const origemDoTm = regraTm.origem === 'fidelidade'
    ? ' — TM das linhas renovadas, pela fidelidade na conta'
    : !enquadramento.renovacao_total
      ? ' — TM médio do CNPJ: a conta não traz a fidelidade de todas as linhas renovadas'
      : '';
  if (
    renovacaoExigeAparelho(mesesRegra, classificacaoCarteira)
    && !linhasDaComposicao.some((linha) => Boolean(linha.aparelho))
  ) pendencias.push(`no M${mesesRegra} a renovação exige pelo menos um aparelho no pedido${mesesRegra >= 15 ? ` (sem aparelho no M${mesesRegra} só para classificação CG)` : ''}${origemDoTm}`);
  if (renovacoesSemNumero > 0) {
    pendencias.push(
      renovacoesSemNumero === 1
        ? 'há uma linha de renovação sem telefone — preencha o número ou remova a linha'
        : `há ${renovacoesSemNumero} linhas de renovação sem telefone — preencha o número ou remova as linhas`,
    );
  }
  const chaveLinha = (v: unknown) => soDigitosLinha(v).replace(/^55(?=\d{10,11}$)/, '');
  const formatarLinha = (v: unknown) => {
    const d = chaveLinha(v);
    return d.length >= 10 ? `(${d.slice(0, 2)}) ${d.slice(2, -4)}-${d.slice(-4)}` : String(v ?? '');
  };
  const calculadasPorNumero = new Map(linhasCalculadas.map((linha) => [chaveLinha(linha.msisdn), linha]));
  const semCalculo = linhasRenovadasNaProposta.filter((linha) => !calculadasPorNumero.has(chaveLinha(linha.numero)));
  const foraDaConta = linhasCalculadas.filter((linha) => Number(linha.ticket_atual || 0) <= 0);
  const semPlano = linhasCalculadas.filter(
    (linha) => Number(linha.ticket_atual || 0) > 0
      && (!linha.plano_novo?.trim() || Number(linha.receita_renovada || 0) <= 0 || linha.faixa_apurada === null),
  );
  const listar = (numeros: unknown[]) => numeros.map(formatarLinha).join(', ');
  const naoEstaoNaConta = [...semCalculo.map((l) => l.numero), ...foraDaConta.map((l) => l.msisdn)];
  if (naoEstaoNaConta.length > 0) {
    pendencias.push(
      naoEstaoNaConta.length === 1
        ? `a linha ${listar(naoEstaoNaConta)} não está na conta importada — importe a conta dela ou tire da renovação`
        : `as linhas ${listar(naoEstaoNaConta)} não estão na conta importada — importe a conta delas ou tire da renovação`,
    );
  }
  if (semPlano.length > 0) {
    pendencias.push(`escolha o plano novo da${semPlano.length === 1 ? ' linha' : 's linhas'} ${listar(semPlano.map((l) => l.msisdn))}`);
  }
  if (
    renovacaoMantemPacote(mesesRegra, classificacaoCarteira)
    && linhasCalculadas.some((linha) => linha.gb_novo === null || Number(linha.gb_novo) < Number(linha.gb_atual || 0))
  ) pendencias.push(`no M${mesesRegra} o plano novo deve manter ou aumentar o pacote de GB de cada linha${origemDoTm}`);
  const numerosRenovados = new Set(linhasCalculadas.map((linha) => String(linha.msisdn || '').replace(/\D/g, '')));
  const composicao = leadComTempo?.proposta_composicao_cnpj as { linhas?: Array<{ tipo?: string; linhaVinculada?: string | null }> } | null;
  const temServicoVinculadoForaDaRenovacao = (composicao?.linhas || []).some((linha) => {
    if (linha.tipo !== 'SERVICO' || !linha.linhaVinculada) return false;
    const numero = String(linha.linhaVinculada).replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
    return numero.length >= 10 && !numerosRenovados.has(numero);
  });
  if (temServicoVinculadoForaDaRenovacao) {
    pendencias.push('revise a linha vinculada dos serviços adicionais');
  }
  if (enquadramento.flag_convergencia_ato === 'SIM') {
    if (!enquadramento.pedido_fixa_vinculado_id) {
      pendencias.push('vincule o pedido de Fixa usado na convergência');
    } else {
      const { data: itensFixa } = await supabase
        .from('v_planilha_mae')
        .select('cnpj, produto, quadro_atual, data_ativacao')
        .eq('pedido_id', enquadramento.pedido_fixa_vinculado_id)
        .in('produto', ['FIBRA', 'FIBRA MIGRA', 'FIBRA NEXT', 'FIBRA UP'])
        .neq('quadro_atual', 'PEDIDO_CANCELADO');
      const fixaValida = (itensFixa || []).filter((item) =>
        String(item.cnpj || '').replace(/\D/g, '') === enquadramento.cnpj,
      );
      if (fixaValida.length === 0) {
        pendencias.push('o pedido de Fixa vinculado precisa ser do mesmo CNPJ e estar ativo');
      } else if (
        fixaValida.every((item) => item.data_ativacao)
        && !fixaValida.some((item) => String(item.data_ativacao).slice(0, 7) === enquadramento.mes_competencia)
      ) {
        pendencias.push('a Fixa vinculada foi instalada fora da competência da renovação');
      }
    }
  }

  if (pendencias.length > 0) {
    return {
      valido: false,
      erro: 'Renovação ainda não pode virar pedido',
      descricao: pendencias.join(' · '),
    };
  }

  return { valido: true };
}

/**
 * Valida se o lead tem os campos obrigatórios preenchidos para enviar proposta
 * Requisitos: nome e telefone do contato real
 */
export function validarContatoRealParaProposta(lead: {
  contato_real_nome?: string | null;
  contato_real_telefone?: string | null;
}): ValidacaoLeadResult {
  // 1. Validar nome do contato real
  if (!lead.contato_real_nome?.trim()) {
    return {
      valido: false,
      erro: 'Contato Real obrigatório',
      descricao: 'Preencha o nome do Contato Real antes de enviar proposta.'
    };
  }
  
  // 2. Validar telefone do contato real
  const telefone = lead.contato_real_telefone?.replace(/\D/g, '');
  if (!telefone || telefone.length < 10) {
    return {
      valido: false,
      erro: 'Telefone do Contato Real obrigatório',
      descricao: 'Preencha o telefone do Contato Real (mínimo 10 dígitos) antes de enviar proposta.'
    };
  }

  return { valido: true };
}

/**
 * PEDIDO exige PROPOSTA — não vale retorno nem fechamento no lugar dela.
 *
 * A régua antiga aceitava qualquer uma das três ações (PROPOSTA_ENVIADA,
 * RETORNO_ENVIADO, FECHAMENTO_ENVIADO), e era por aí que o pedido nascia sem
 * proposta: o consultor salvava os valores e disparava direto o fechamento.
 * Foi o caso do lead ANNE COSTA ODONTOLOGIA em 04/08 — valores salvos 16:40:29,
 * fechamento enviado 16:40:36, lead convertido 16:40:53, e nenhuma proposta em
 * nenhum momento. Medido nos 30 dias anteriores: de 2.471 pedidos vindos de
 * lead, 1.061 passaram só com retorno/fechamento.
 *
 * Douglas 05/08: "não pode passar mais um pedido sem proposta enviada e ter o
 * vínculo com o CRM whats inbox". Daí as duas exigências: a ação
 * PROPOSTA_ENVIADA no histórico E a conversa do Inbox gravada no lead
 * (`url_conversa_bee`), que é a prova de que a proposta saiu por aqui.
 *
 * E a conversa tem que ser com o CLIENTE. Douglas 05/08, depois de ver os 96
 * pedidos criados com a linha do próprio consultor no lugar do telefone do
 * cliente: "a proposta tem que ir para o cliente" — ter conversa não basta se
 * ela é com a gente mesmo.
 */
/**
 * Valida se o lead pode ser convertido em pedido (status FECHADO)
 * Requisitos:
 * 1. Contato Real completo (nome + telefone)
 * 2. PROPOSTA enviada pelo WhatsApp Inbox do CRM, com a conversa vinculada
 *    comprovada contra os produtos e condições atuais.
 * 3. Lead não pode já ter sido convertido (pedido_id ou status CONVERTIDO)
 */
export async function validarLeadParaPedido(lead: {
  id: string;
  contato_real_nome?: string | null;
  contato_real_telefone?: string | null;
  pedido_id?: string | null;
  status_lead?: string | null;
  tipo_lead?: string | null;
}): Promise<ValidacaoLeadResult> {
  // 1. Verificar duplicata
  if (lead.pedido_id || lead.status_lead === 'CONVERTIDO') {
    return {
      valido: false,
      erro: 'Lead já convertido',
      descricao: 'Este lead já foi convertido em pedido anteriormente.'
    };
  }

  // 2. Validar contato real
  const validacaoContato = validarContatoRealParaProposta(lead);
  if (!validacaoContato.valido) {
    return {
      valido: false,
      erro: validacaoContato.erro,
      descricao: validacaoContato.descricao?.replace('enviar proposta', 'criar o pedido')
    };
  }

  // 3. O telefone do contato NÃO pode ser uma linha nossa
  if (await ehNumeroDaCasa(lead.contato_real_telefone)) {
    return {
      valido: false,
      erro: 'Contato Real com número do CRM',
      descricao: MENSAGEM_NUMERO_DA_CASA
    };
  }

  // A régua da renovação é o PRODUTO da proposta, não o tipo do lead (Douglas
  // 06/09/2026). `validarRenovacaoParaPedido` já lê a composição e devolve
  // válido quando não há linha RENOVACAO — chamar sempre é o que faz a trava
  // valer também em portabilidade, TAB e TT, que hoje viram pedido sem
  // enquadramento nenhum.
  const validacaoRenovacao = await validarRenovacaoParaPedido(lead.id);
  if (!validacaoRenovacao.valido) return validacaoRenovacao;

  const { data: propostaServico } = await supabase
    .from('leads_carteira')
    .select('proposta_composicao_cnpj')
    .eq('id', lead.id)
    .maybeSingle();
  const linhasServico = ((propostaServico?.proposta_composicao_cnpj as { linhas?: LinhaServicoMatch[] } | null)?.linhas) || [];
  const problemaServico = mensagemProblemaServico(conferirServicoNaLinha(linhasServico));
  if (problemaServico) {
    return { valido: false, erro: 'Serviço digital fora da linha', descricao: problemaServico };
  }

  // 4. Proposta enviada pelo Inbox do CRM, para o número do cliente
  try {
    const { conferirPropostaAtual } = await import('./conferirPropostaAtual');
    const conferencia = await conferirPropostaAtual(lead.id);
    return conferencia.ok ? { valido: true } : {
      valido: false, erro: 'Confira a versão da proposta',
      descricao: `${conferencia.motivo} ${conferencia.acao} Código: ${conferencia.codigo}`,
    };
  } catch (error) {
    return { valido: false, erro: 'Conferência indisponível', descricao: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Busca dados do lead do banco e valida para criação de pedido
 * Útil quando só temos o leadId
 */
export async function buscarEValidarLeadParaPedido(leadId: string): Promise<ValidacaoLeadResult & { lead?: LeadValidado }> {
  const { data: lead, error } = await supabase
    .from('leads_carteira')
    .select('id, contato_real_nome, contato_real_telefone, pedido_id, status_lead, tipo_lead')
    .eq('id', leadId)
    .single();

  if (error || !lead) {
    return {
      valido: false,
      erro: 'Lead não encontrado',
      descricao: 'Não foi possível carregar os dados do lead.'
    };
  }

  const validacao = await validarLeadParaPedido(lead);
  return { ...validacao, lead };
}
